/**
 * shared/ladder-floor-codes.ts — the one place the server and the client agree
 * on how a ladder-floor refusal is spelled on the wire (AC-22, spec
 * `2026-08-31-security-tier-ladder` module 04).
 *
 * Shared for exactly the reason `shared/possession-proof.ts` is: the server
 * EMITS these (`server/logic/security-tier-ladder/floor-enforcement.ts`) and
 * the client MATCHES on them (`client/src/lib/queryClient.ts`) to decide
 * whether a refusal is one a single passkey tap can clear. Two independent
 * string literals is the shape where one side is renamed and the other
 * silently stops matching — and the failure here is not a broken screen but a
 * user staring at a 403 with no offered way to satisfy it.
 *
 * NOT session-death codes, and not `POSSESSION_PROOF_REQUIRED`. The session is
 * alive and its cookie is fine; what is missing is a recent enough tap, or
 * enough enrolment. `isSessionDeathCode` must never admit these values.
 */

/** 403 code: the ladder floor for this action refused this session. */
export const LADDER_FLOOR_REQUIRED = "LADDER_FLOOR_REQUIRED" as const;

/** 403 code: the floor could not be decided at all, so the action was refused
 *  (fail-closed). Deliberately DISTINCT from the above — "you need a stronger
 *  session" and "this gate could not run" are different things for a client to
 *  act on, and only the first is ever fixable by the person holding the phone. */
export const LADDER_FLOOR_UNAVAILABLE = "LADDER_FLOOR_UNAVAILABLE" as const;

/**
 * The refusal reasons a single fresh possession proof can genuinely clear —
 * and ONLY those. A client may run the possession heartbeat and retry once for
 * these; for anything else a retry is a passkey prompt that cannot possibly
 * change the answer, which is worse than an honest refusal.
 *
 * `level_too_low` is deliberately ABSENT. Its causes look identical from
 * here: an account that has not enrolled enough to reach the required level
 * (a tap changes nothing — the fix is enrolment); a session whose context
 * stamp did not match, which `sessionLevel` forces to Level 1 (AC-15) and
 * which a tap also cannot fix, since the mismatch is about WHERE the request
 * came from; and — reachable since floor-2 rows exist (ledger #133) — a
 * session with no qualifying tap at all, or a ceiling-1 account
 * (guardian-only / zero passkeys), where a mid-session prompt is at best a
 * detour through a full fresh sign-in and at worst can change nothing.
 * Prompting on this reason is a dead end dressed up as an action.
 */
export const RETRIABLE_LADDER_FLOOR_REASONS = [
  // Level 3+ floor cleared on level, but the last tap is outside the Fresh band.
  "settled_band_high_stakes",
  // A row declaring `freshness: "fresh"` below the AC-13 threshold, same cause.
  "row_requires_fresh_band",
  // A freshness claim the session proof itself denies — a re-proof resolves it.
  "tap_timestamp_without_key_tap",
] as const;

export type RetriableLadderFloorReason = (typeof RETRIABLE_LADDER_FLOOR_REASONS)[number];

/** Structural check against an untrusted response body's `reason` field. */
export function isRetriableLadderFloorReason(
  reason: unknown,
): reason is RetriableLadderFloorReason {
  return (
    typeof reason === "string" &&
    (RETRIABLE_LADDER_FLOOR_REASONS as readonly string[]).includes(reason)
  );
}
